import { useEffect, useRef, useState } from "react";
import {
  getDefaultPhoneCountry,
  getNationalDigitsFromPhoneValue,
  getNationalMobileMaxDigits,
  isCallingCodeOnlyValue,
  limitPhoneValueToCountryMax,
  parsePhoneNumber,
  toE164FromLocalDigits,
} from "@/utils/phoneInput";
import styles from "./PhoneInputField.module.css";

export default function PhoneInputField({
  value,
  onChange,
  defaultCountry = getDefaultPhoneCountry(),
  error = false,
  className = "",
  id,
  name,
  onBlur,
  onKeyDown,
  placeholder = "Enter mobile number",
  disabled = false,
  keepNationalOnCountryChange = false,
  onCountryChange,
}) {
  const [PhoneInput, setPhoneInput] = useState(null);
  const [country, setCountry] = useState(defaultCountry);
  const nationalRef = useRef("");
  const ignoreCountryWipeRef = useRef(false);

  useEffect(() => {
    let active = true;
    import("react-phone-number-input").then((mod) => {
      if (active) setPhoneInput(() => mod.default);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const { national } = getNationalDigitsFromPhoneValue(value, country);
    if (national.length >= 6) nationalRef.current = national;

    try {
      const parsed = value ? parsePhoneNumber(value) : null;
      if (parsed?.country) setCountry(parsed.country);
    } catch {
      // keep current country
    }
  }, [value, country]);

  const handleChange = (nextValue) => {
    if (keepNationalOnCountryChange && ignoreCountryWipeRef.current) {
      if (!nextValue || isCallingCodeOnlyValue(nextValue, country)) return;
    }

    if (
      keepNationalOnCountryChange &&
      nationalRef.current.length >= 6 &&
      (!nextValue || isCallingCodeOnlyValue(nextValue, country))
    ) {
      return;
    }

    let nextCountry = country;
    try {
      const parsed = nextValue ? parsePhoneNumber(nextValue) : null;
      if (parsed?.country) nextCountry = parsed.country;
    } catch {
      // keep current country
    }

    const limited = limitPhoneValueToCountryMax(nextValue, nextCountry);
    const { national } = getNationalDigitsFromPhoneValue(limited, nextCountry);
    if (national.length >= 6) nationalRef.current = national;
    onChange?.(limited);
  };

  const maxNationalDigits = getNationalMobileMaxDigits(country);

  const blockExtraDigits = (event) => {
    const data = event.data;
    if (!data || !/\d/.test(data)) return;
    const { national } = getNationalDigitsFromPhoneValue(value, country);
    if (national.length >= maxNationalDigits) {
      event.preventDefault();
    }
  };

  const handleCountryChange = (nextCountry) => {
    if (!nextCountry) return;
    setCountry(nextCountry);
    onCountryChange?.(nextCountry);

    if (!keepNationalOnCountryChange) {
      if (value) onChange?.(limitPhoneValueToCountryMax(value, nextCountry));
      return;
    }

    const preserved =
      nationalRef.current ||
      getNationalDigitsFromPhoneValue(value, country).national;
    if (preserved.length < 6) return;

    ignoreCountryWipeRef.current = true;
    nationalRef.current = preserved;
    onChange?.(toE164FromLocalDigits(preserved, nextCountry));
    window.setTimeout(() => {
      ignoreCountryWipeRef.current = false;
    }, 0);
  };

  const wrapClass = [
    styles.wrap,
    error ? styles.error : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (!PhoneInput) {
    return (
      <div className={wrapClass}>
        <input
          type="tel"
          className={styles.fallback}
          placeholder={placeholder}
          disabled
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <PhoneInput
        id={id}
        name={name}
        international
        limitMaxLength
        defaultCountry={country || defaultCountry}
        value={value}
        onChange={handleChange}
        onCountryChange={handleCountryChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={styles.phoneInput}
        numberInputProps={{
          inputMode: "numeric",
          onBeforeInput: blockExtraDigits,
          onKeyDown,
        }}
      />
    </div>
  );
}
